import React, { useCallback, useState } from 'react';
import useResizeObserver from 'hooks/use-resize-observer';
import { useMemo } from 'react';
import {
  HighlightArea,
  HorizontalGridLines,
  LabelSeries,
  LineSeries,
  VerticalGridLines,
  XAxis,
  XYPlot,
  YAxis,
} from 'react-vis';
import {
  getColorForIdx,
  getLabelForIdx,
  getMarkerForIdx,
  GRID_LINES_STYLE,
  LR_MARGIN,
  LR_MARGIN_WITH_TICKS,
  SERIES_LABEL_PADDING,
  TICK_LINE_AXIS_STYLE,
} from './timeseriesplot';
import {
  CumulativePlotReadings,
  ObservationPointDecorated,
} from 'util/backendapi/types/Model';
import { flatMap, min, range, sortBy, throttle } from 'lodash';
import moment from 'moment';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import CrosshairWithReticle from './crosshairwithreticle';
import { detectExportMode } from 'util/export';
import { isNotNull } from 'util/validation';
import { Popup } from './popups/Popup';
import {
  CumulativePlotMouseoverPoint,
  CumulativePlotReadingInfo,
} from './popups/CumulativePlotReadingInfo';
import { SelectionArea } from './SelectionArea';

export interface CumulativePlotZoomParams {
  xDomain: number[];
  yDomain: number[];
}

export type CumulativePlotOnZoomFn = (
  zoomParams: CumulativePlotZoomParams
) => void;

interface CumulativePlotProps {
  observationPoint: ObservationPointDecorated;
  itemNumber: number;
  cumulativePlotReadings: CumulativePlotReadings;
  timePeriods: number[];
  width?: number;
  height?: number;
  onZoom?: CumulativePlotOnZoomFn;
  zoomArea: CumulativePlotZoomParams | null;
}

const Y_AXIS_DOMAIN_SCALE_FACTOR = 1.05;

const axisStyle = {
  line: {
    stroke: '#cfcfcf',
  },
};

const tickSize = {
  tickSizeOuter: 0,
  tickSizeInner: 5,
};

const WEEKLY_GRID_LINES_STYLE = {
  stroke: '#efefef',
  strokeDasharray: '1',
};

const WEEKLY_GRID_LINE_DAYS_THRESHOLD = 100;

type CumulativePlotDataSeries = {
  year: number;
  readings: {
    x: number;
    y: number;
  }[];
};

function selectMouseoverPoints(
  dataSeries: CumulativePlotDataSeries[],
  zoomArea: CumulativePlotZoomParams | null
): CumulativePlotMouseoverPoint[] {
  let allSeriesReadings: (CumulativePlotMouseoverPoint | null)[] = [];

  for (let seriesIdx = 0; seriesIdx < dataSeries.length; seriesIdx++) {
    const serie = dataSeries[seriesIdx];

    const seriesColor = getColorForIdx(seriesIdx);
    const marker = getMarkerForIdx(seriesIdx);

    for (let readingIdx = 0; readingIdx < serie.readings.length; readingIdx++) {
      const dataPoint = serie.readings[readingIdx];

      if (
        zoomArea &&
        (dataPoint.x < zoomArea.xDomain[0] ||
          dataPoint.x > zoomArea.xDomain[1] ||
          dataPoint.y < zoomArea.yDomain[0] ||
          dataPoint.y > zoomArea.yDomain[1])
      ) {
        continue;
      }

      allSeriesReadings.push({
        ...dataPoint,
        year: serie.year,
        seriesColor,
        marker,
      });
    }
  }

  return allSeriesReadings.filter(isNotNull);
}

export function CumulativePlot(props: CumulativePlotProps) {
  const {
    cumulativePlotReadings,
    observationPoint,
    timePeriods,
    zoomArea,
    onZoom,
  } = props;

  const [isBrushing, setIsBrushing] = useState<boolean>(false);
  const [hoverPoint, setHoverPoint] =
    useState<null | CumulativePlotMouseoverPoint>(null);
  const [selectedPoint, setSelectedPoint] =
    useState<null | CumulativePlotMouseoverPoint>(null);

  const [isHoverable, setIsHoverable] = useState(true);

  const onNearestXY = useMemo(
    () =>
      throttle((hoverPoint: CumulativePlotMouseoverPoint | null) => {
        setHoverPoint(hoverPoint);
      }, 1000 / 12),
    []
  );

  const onClosePopup = useCallback(() => {
    setSelectedPoint(null);
    setTimeout(() => {
      setIsHoverable(true);
    }, 100);
  }, []);

  const [plotWrapperRef, plotWrapperWidth, plotWrapperHeight] =
    useResizeObserver<HTMLDivElement>({
      defaultWidth: 500,
      defaultHeight: 500,
    });

  const plotSize = useMemo(() => {
    const size: {
      height: number;
      width: number;
    } = {
      height: plotWrapperHeight,
      width: plotWrapperWidth,
    };

    if (props.width) {
      size.width = props.width;
    }

    if (props.height) {
      size.height = props.height;
    }
    return size;
  }, [plotWrapperHeight, plotWrapperWidth, props.width, props.height]);

  const dataSeries = useMemo(() => {
    return sortBy(Object.keys(cumulativePlotReadings.readings)).map((year) => {
      const readings = cumulativePlotReadings.readings[year].map((d, i) => ({
        x: d.doy,
        y: d.value,
      }));
      return { year: +year, readings: readings };
    });
  }, [cumulativePlotReadings]);

  const { xDomain, yDomain } = useMemo(() => {
    const hasLeapYear = dataSeries.some((d) => moment([d.year]).isLeapYear());

    const xDomain = zoomArea ? zoomArea.xDomain : [0, hasLeapYear ? 366 : 365];
    const yDomain = zoomArea
      ? zoomArea.yDomain
      : [
          0,
          Math.max(
            ...flatMap(
              dataSeries.map((series) =>
                series.readings.map((d) => d.y * Y_AXIS_DOMAIN_SCALE_FACTOR)
              )
            )
          ),
        ];
    return { xDomain, yDomain };
  }, [zoomArea, dataSeries]);

  const mouseOverDataPoints = useMemo(
    () => selectMouseoverPoints(dataSeries, zoomArea),
    [dataSeries, zoomArea]
  );

  const xAxisTitle = i18n._(t`Days since 1st January`);
  const yAxisTitle =
    observationPoint.instrument_type.cumulative_plot_y_axis_title ||
    observationPoint.instrument_type.default_vertical_plot_title;

  const startYear = useMemo(() => min(timePeriods)!, [timePeriods]);

  const { weeklyGridLines } = useMemo(() => {
    // Show weekly grid lines (mondays of start year) if zoomed in sufficiently
    if (
      !zoomArea ||
      zoomArea.xDomain[1] - zoomArea.xDomain[0] >
        WEEKLY_GRID_LINE_DAYS_THRESHOLD
    ) {
      return {
        weeklyGridLines: null,
      };
    }

    const startDate = moment(new Date(startYear, 0, 1)).date(
      zoomArea.xDomain[0]
    );

    const nextMonday = moment(startDate).isoWeekday(8);

    let weeklyGridLines: number[] = [];

    let date = nextMonday;
    while (date.year() === startYear && date.date() <= zoomArea.xDomain[1]) {
      weeklyGridLines.push(date.dayOfYear());
      date = date.add(7, 'days');
    }

    return { weeklyGridLines };
  }, [startYear, zoomArea]);

  const { monthTicks, monthLabels } = useMemo(() => {
    const monthNames = range(12).map((i) =>
      moment(new Date(startYear, i, 1)).format('MMM')
    );
    const monthDays = range(12).map((i) =>
      moment(new Date(startYear, i, 1)).daysInMonth()
    );

    const monthTicks: number[] = [];
    const monthLabels: string[] = [];
    let sumDays = 0;

    for (let i = 0; i < 12; i++) {
      const tickDay = sumDays;
      sumDays += monthDays[i];

      if (
        zoomArea &&
        (tickDay < zoomArea.xDomain[0] - 1 || tickDay > zoomArea.xDomain[1] + 1)
      ) {
        // Don't show label if outside zoom area
        continue;
      }

      monthTicks.push(tickDay);
      monthLabels.push(monthNames[i]);
    }

    return { monthTicks, monthLabels };
  }, [startYear, zoomArea]);

  // Zoom selection area changes
  const handleBrushEnd = useCallback(
    (area: HighlightArea<number, number> | null) => {
      setIsBrushing(false);
      if (!area || !onZoom) {
        return;
      }
      const newZoomArea: CumulativePlotZoomParams = {
        xDomain: [area.left, area.right],
        yDomain: [area.bottom, area.top],
      };

      onZoom(newZoomArea);
    },
    [onZoom]
  );

  const exportMode = detectExportMode();

  return (
    <>
      <div className="plot-area" ref={plotWrapperRef}>
        <XYPlot
          xDomain={xDomain}
          yDomain={yDomain}
          margin={{ left: LR_MARGIN_WITH_TICKS, right: LR_MARGIN }}
          {...plotSize}
        >
          <HorizontalGridLines style={GRID_LINES_STYLE} />
          <VerticalGridLines style={GRID_LINES_STYLE} tickValues={monthTicks} />
          {weeklyGridLines && (
            <VerticalGridLines
              style={WEEKLY_GRID_LINES_STYLE}
              tickValues={weeklyGridLines}
            />
          )}
          <XAxis
            orientation="top"
            className="xAxis"
            position="middle"
            hideTicks
            style={TICK_LINE_AXIS_STYLE}
          />
          <YAxis
            orientation="right"
            className="yAxis"
            hideTicks
            style={TICK_LINE_AXIS_STYLE}
          />
          <XAxis
            title={xAxisTitle}
            top={plotSize.height}
            orientation="bottom"
            position="middle"
            hideLine
            hideTicks
          />
          <YAxis
            title={yAxisTitle}
            left={0}
            marginLeft={0}
            orientation="left"
            position="middle"
            hideLine
            hideTicks
          />
          <XAxis
            style={axisStyle}
            {...tickSize}
            className="xAxis"
            tickFormat={(_, idx) => monthLabels[idx]}
            tickValues={monthTicks}
          />

          {/* Cumulative plot lines. */}
          {dataSeries.map((series, idx) => (
            <LineSeries
              key={`line-${series.year}`}
              animation={false}
              className="plot-readings-line"
              style={{ stroke: getColorForIdx(idx) }}
              data={series.readings}
            />
          ))}

          {/* Letter labels (A, B, C, etc) at end of each data series line. */}
          {dataSeries.map((series, idx) => {
            const lastReading = series.readings[series.readings.length - 1];
            const letterPlotPoint = {
              x: lastReading.x,
              y: lastReading.y,
              label: getLabelForIdx(idx),
              xOffset: SERIES_LABEL_PADDING,
            };
            return (
              <LabelSeries<{
                x: number;
                y: number;
                label: string;
                xOffset: number;
              }>
                key={`label-${idx}`}
                data={[letterPlotPoint]}
                allowOffsetToBeReversed={false}
                labelAnchorY="middle"
                style={{
                  fill: getColorForIdx(idx),
                  stroke: getColorForIdx(idx),
                }}
                xDomain={xDomain}
                yDomain={yDomain}
              />
            );
          })}

          {/* Mouse over data points */}
          {!isBrushing &&
            isHoverable &&
            !exportMode &&
            mouseOverDataPoints.length > 0 && (
              <LineSeries<CumulativePlotMouseoverPoint>
                animation={false}
                data={mouseOverDataPoints}
                className="plot-readings-invisible-line"
                strokeWidth={0}
                onNearestXY={onNearestXY}
              />
            )}

          {/* Vertical "hover" line */}
          {!isBrushing && hoverPoint && (
            <CrosshairWithReticle
              showVertical={true}
              showHorizontal={false}
              values={[hoverPoint]}
              onReticleClick={(point, position) => {
                setSelectedPoint({
                  ...point,
                  position,
                });
                setIsHoverable(false);
              }}
            />
          )}
          {/* Selected point detail popup */}
          {selectedPoint && (
            <Popup point={selectedPoint}>
              <CumulativePlotReadingInfo
                point={selectedPoint}
                observationPoint={observationPoint}
                onClose={onClosePopup}
              />
            </Popup>
          )}
          {/* Zoom area selection */}
          {!selectedPoint && !exportMode && (
            <SelectionArea<number, number>
              onBrushStart={async () => {
                setIsBrushing(true);
                setHoverPoint(null);
              }}
              onBrushEnd={handleBrushEnd}
              className={'plot-zoom'}
            />
          )}
          <YAxis style={axisStyle} {...tickSize} className="yAxis" />
        </XYPlot>
      </div>
      <div className="plot-legend plot-legend-cumulative">
        <ol>
          {dataSeries.map((series, idx) => (
            <div key={idx}>
              <li>
                <span style={{ color: getColorForIdx(idx) }}>
                  {getLabelForIdx(idx)}
                </span>{' '}
                {getMarkerForIdx(idx) && (
                  <>
                    <span
                      className="plot-marker-legend"
                      style={{ color: getColorForIdx(idx) }}
                    ></span>{' '}
                  </>
                )}
                {observationPoint.code} {series.year}
              </li>
            </div>
          ))}
        </ol>
      </div>
    </>
  );
}
